import React, { useState, useEffect } from 'react';
import { BaseCard } from '../UI/BaseCard';
import { BaseInput } from '../UI/BaseInput';
import { BaseButton } from '../UI/BaseButton';
import { BaseSelect } from '../UI/BaseSelect';
import { __, sprintf } from '@wordpress/i18n';
import { BasePetSelector } from '../UI/BasePetSelector';
import { IconDogGeneral } from '../Icons/IconDogGeneral';
import { IconCatGeneral } from '../Icons/IconCatGeneral';

import { useForm } from '../../hooks/useForm';
import { maxValueValidator, minValueValidator, numbersValidator, requiredFieldValidator } from '../../utils/validation';

export const CalculatorIntake = () => {
  const [result, setResult] = useState(null);

  const { formValue, formErrors, setFormFieldValue, setFormValue, onFormSubmit } = useForm({
    form: {
      kindInput: '',
      weight: '',
      petType: 'dog'
    },
    rules: {
      kindInput: requiredFieldValidator(),
      weight: [minValueValidator(0), numbersValidator(), maxValueValidator(100), requiredFieldValidator()],
      petType: [requiredFieldValidator()]
    },
    onSubmit: () => {
      getCalculatorResult(formValue);
    }
  });

  useEffect(() => {
    setResult(null);
  }, [formValue]);

  const petTypes = [
    {
      value: 'dog',
      icon: <IconDogGeneral />
    },
    {
      value: 'cat',
      icon: <IconCatGeneral />
    }
  ];

  const intakeParamValues = [
    {
      value: '1.6',
      text: __('Neutered adult', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '1.8',
      text: __('Intact adult', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '1.30',
      text: __('Inactive/obese prone', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '1',
      text: __('Weight loss', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '1.3',
      text: __('Weight gain (mild)', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '1.7',
      text: __('Weight gain', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '2.5',
      text: __('Active/working dog (mild)', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '3.5',
      text: __('Active/working dog (moderate)', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '4.5',
      text: __('Active/working dog (high intensity)', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '3',
      text: __('Puppy 0-4 months', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '2',
      text: __('Puppy 4 months to adult', 'cooperpetcare'),
      type: 'dog'
    },
    {
      value: '2.50',
      text: __('Kitten', 'cooperpetcare'),
      type: 'cat'
    },
    {
      value: '1.2',
      text: __('Neutered adult', 'cooperpetcare'),
      type: 'cat'
    },
    {
      value: '1.4',
      text: __('Intact adult', 'cooperpetcare'),
      type: 'cat'
    },
    {
      value: '1',
      text: __('Inactive/obese prone', 'cooperpetcare'),
      type: 'cat'
    },
    {
      value: '0.8',
      text: __('Weight loss', 'cooperpetcare'),
      type: 'cat'
    }
  ];

  const getCalculatorResult = ({ kindInput, weight }) => {
    const kindInputParams = intakeParamValues.find(({ value }) => value === kindInput);

    const rer = 70 * Math.pow(weight, 3 / 4);
    const value = Math.round(rer * kindInputParams.value);
    setResult({ title: __('Daily Calorie Intake Recommendation:', 'cooperpetcare'), answer: value });
  };

  const onSelectPetType = (petType) => {
    setFormValue({
      ...formValue,
      petType,
      kindInput: '',
      weight: ''
    });
    setResult(null);
  };

  return (
    <div className="calculator calculator--intake">
      <BaseCard title={__('Daily Calorie Intake Calculator', 'cooperpetcare')}>
        <BasePetSelector items={petTypes} value={formValue.petType} size="small" onSelect={onSelectPetType} />

        <BaseSelect
          className="field persistent-placeholder"
          label={__('Life stage:', 'cooperpetcare')}
          placeholder={__('Please select an option', 'cooperpetcare')}
          persistentPlaceholder={true}
          isInvalid={!!formErrors.kindInput}
          hint={formErrors.kindInput}
          options={intakeParamValues.filter((item) => item.type === formValue.petType)}
          value={formValue.kindInput}
          onSelect={setFormFieldValue('kindInput')}
        ></BaseSelect>

        <BaseInput
          className="field persistent-placeholder"
          label={__('Weight of the pet (kg):', 'cooperpetcare')}
          placeholder={__('Weight of your pet in kilograms', 'cooperpetcare')}
          type="number"
          persistentPlaceholder={true}
          isInvalid={!!formErrors.weight}
          hint={formErrors.weight}
          value={formValue.weight}
          onChange={setFormFieldValue('weight')}
        ></BaseInput>

        <BaseButton className="btn-rounded" variant="warning" onClick={onFormSubmit}>
          {__('Calculate', 'cooperpetcare')}
        </BaseButton>

        {result && (
          <div className="result">
            <div className="result-description result-list-item">
              <b>{result.title}</b> <br /> {result.answer} {__('Callories', 'cooperpetcare')}
            </div>
          </div>
        )}
      </BaseCard>
    </div>
  );
};
